import { Client } from "@microsoft/microsoft-graph-client";

export class SharePointService {

  constructor(userInstance) {
    this.userInstance = userInstance;

    // Initialize the Graph client
    this.graphClient = Client.initWithMiddleware({
      authProvider: {
        getAccessToken: async () => await this.getGraphAccessToken()
      }
    });
  }

  async initialize() {
    this.siteId = await this.getSiteId();
  }

  async getGraphAccessToken() {
    return await this.userInstance.getGraphToken();
  }

  async getUserProfile() {
    try {
      const response = await this.graphClient
        .api(`/me`)
        .get();

      if (response) {
        return response;
      } else {
        console.error('Error fetching user profile:', response.statusText);
      }
    } catch (error) {
      console.error("Failed to retrieve list ID:", error);

      return null;
    }
  }

  async getSiteId() {
    let siteId = '';

    try {
      const siteResponse = await this.graphClient.api(`/sites/${process.env.VUE_APP_AZURE_SITE_HOSTNAME}:/${process.env.VUE_APP_AZURE_SITE_RELATIVE_URL}`).get();

      siteId = siteResponse.id;
    } catch (ex) {
      console.log('Could not get site id, error: ', ex);
    }

    return siteId;
  }

  async getListIdByName(listName) {
    try {
      const response = await this.graphClient
        .api(`/sites/${this.siteId}/lists`)
        .get();

      if (response.value && response.value.length > 0) {
        // Manually filter the result
        const matchingList = response.value.find((drive) => drive.name === listName);

        if (matchingList) {
          return matchingList.id; // Return the ID of the first matching list
        } else {
          throw new Error(`List with name '${listName}' not found.`);
        }
      } else {
        throw new Error('No lists found.');
      }
    } catch (error) {
      console.error("Failed to retrieve list ID:", error);
      return null;
    }
  }

  async getItemsForList(listId, query, top = 50) {
    try {
      const itemsResponse = await this.graphClient
          .api(`/sites/${this.siteId}/lists/${listId}/items`)
          .expand('fields')
          .filter(query)
          .top(top)
          .get();

      if (itemsResponse.value && itemsResponse.value.length > 0) {
        return itemsResponse.value; // Return the results from the search
      } else {
        return [];
      }
    } catch (error) {
      console.error("Search failed:", error);

      return [];
    }
  }

  async getItemsForInvoiceList(listId, query, maxItems = 10000, pageSize = 500) {
    const allItems = [];
    const effectivePageSize = Math.min(pageSize, 500);

    try {
      let request = this.graphClient
          .api(`/sites/${this.siteId}/lists/${listId}/items`)
          .header("ConsistencyLevel", "eventual")
          .header("Prefer", "HonorNonIndexedQueriesWarningMayFailRandomly")
          .expand(`fields($select=InvoiceId,InvoiceDate,InvoiceAmount,InvoiceAccount,InvoiceName,InvoicingName,CompanyId,SalesId,CustomerRef,DocumentType,DocumentName,ContractNumber,PaymentMethod,FileLeafRef)`)
          .top(effectivePageSize)
          .orderby("fields/InvoiceDate desc");

      if (query) {
        request = request.filter(query);
      }

      let resp = await request.get();
      if (resp.value?.length) allItems.push(...resp.value);

      let nextLink = resp['@odata.nextLink'];

      while (nextLink && allItems.length < maxItems) {
        resp = await this.graphClient
            .api(nextLink)
            .header("ConsistencyLevel", "eventual")
            .header("Prefer", "HonorNonIndexedQueriesWarningMayFailRandomly")
            .get();

        if (resp.value?.length) allItems.push(...resp.value);
        nextLink = resp['@odata.nextLink'];
      }

      return allItems.slice(0, maxItems);
    } catch (e) {
      console.error("Search failed:", e?.statusCode, e?.code, e?.message, e?.body?.error ?? e);
      return [];
    }
  }

  async downloadFile(listId, itemId, fileName) {
    try {
      const fileEndpoint = `/sites/${this.siteId}/lists/${listId}/items/${itemId}/driveItem/content`;

      const response = await this.graphClient.api(fileEndpoint).responseType('blob').get();

      if (!(response instanceof Blob)) {
        throw new Error('The response is not a Blob.');
      }

      return response;
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }

  async updateListItemVisibility(listId, itemId) {
    try {
      const updatePayload = {
        'Portal': 'No',
      };

      const response = await this.graphClient
        .api(`/sites/${this.siteId}/lists/${listId}/items/${itemId}/fields`)
        .patch(updatePayload);

      console.log('Field updated successfully:', response);

      return true;
    } catch (error) {
      console.error("Failed to update the item field:", error);

      return false;
    }
  }
}